/**
 * @file areaSelectors.ts
 * @description Shared area selectors used by both container.ts and room.ts
 *
 * This file was created to break the circular dependency between container.ts and room.ts.
 * These selectors are imported by both modules and provide access to area-related state.
 */

import { createSelector } from "@reduxjs/toolkit";
import { createCachedSelector } from 're-reselect';
import { getStoreProp } from "utilities/string";
import { get, ceil, round, isString, remove, omit, values } from "lodash";
import { RootState } from "types/redux/common";

/**
 * Internal selector to get a UI property (inlined to avoid circular dependency with ui.ts)
 */
const getUIProperty = createCachedSelector(
  (state: RootState) => state.ui,
  (state: RootState, property: string) => property,
  (uiState, property) => uiState?.[getStoreProp(property) as any]
)(
  (state: RootState, property: string) => property
);

/**
 * Selects the active tab from the state.
 * @param {Object} state - The Redux state.
 * @returns {string} The active tab ID.
 */
export const getActiveTab = (state: RootState) => state.container.activeTab;

/**
 * Gets the areas object.
 * @param {Object} state - The Redux state.
 * @returns {Object} The areas object.
 */
export const getAreas = (state: RootState) => state.container.areas;

/**
 * Selects all areas or a specific area based on the active tab.
 * @param {Object} state - The Redux state.
 * @returns {Object|Object[]} Either an array of all areas or a single area object.
 */
export const getAreaArray = createSelector(
  getActiveTab,
  getAreas,
  (tab, areas) => {
    const areaArray = tab in areas ? [areas[tab]] : values(areas);

    // For conference room designer, copy speaker plotData values to top level for getPlotProps
    return areaArray.map(area => {
      if (area.speakers?.plotData) {
        return {
          ...area,
          // Copy SPL averages and legend values to top level for getPlotProps selector
          directAwtdSpl: area.speakers.plotData.directAwtdSpl,
          directAwtdSplStd: area.speakers.plotData.directAwtdSplStd,
          totalAwtdSpl: area.speakers.plotData.totalAwtdSpl,
          totalAwtdSplStd: area.speakers.plotData.totalAwtdSplStd,
          stiWithNoiseMasking: area.speakers.plotData.stiWithNoiseMasking,
          stiWithNoiseMaskingStd: area.speakers.plotData.stiWithNoiseMaskingStd,
          directAwtdSplLegendMin: area.speakers.plotData.directAwtdSplLegendMin,
          directAwtdSplLegendMax: area.speakers.plotData.directAwtdSplLegendMax,
          totalAwtdSplLegendMin: area.speakers.plotData.totalAwtdSplLegendMin,
          totalAwtdSplLegendMax: area.speakers.plotData.totalAwtdSplLegendMax,
        };
      }
      return area;
    });
  }
);

/**
 * Selects a nested property from the container state (excluding areas).
 * @param {Object} state - The Redux state.
 * @param {...string} args - The nested property path to select.
 * @returns {*} The value of the specified nested property from the container state.
 */
const getContainerProperty2 = createCachedSelector(
  (state: RootState) => omit((state as any).container, 'areas'),
  (state: RootState, ...args: any[]) => isString(args) ? args : remove(args, (x) => x !== undefined).join('.'),
  (containerState, property) => get(containerState, getStoreProp(property))
)(
  (state: RootState, ...args: any[]) => isString(args) ? args : remove(args, (x) => x !== undefined).join(':')
);

/**
 * Selects the state of the current area based on the active tab.
 * @param {Object} state - The Redux state.
 * @returns {Object|undefined} The state of the specified area, including properties like
 *                             dimensions, location, ceilingHeight, table, acoustics, etc.
 */
export const getAreaState = createCachedSelector(
  (state: RootState) => state.container.areas,
  getActiveTab,
  (areas, areaId) => areas?.[areaId]
)(
  getActiveTab
);

/**
 * Selects a nested property from an area's state.
 * @param {Object} state - The Redux state.
 * @param {string} property - The property to select from the area state.
 * @returns {*} The value of the specified property from the area state.
 * @function
 * @name getAreaProperty2
 * @type {function(Object, string): *}
 */
export const getAreaProperty2 = createCachedSelector(
  getAreaState,
  (state: RootState, property: string) => property,
  (areaState, property) => get(areaState, getStoreProp(property))
)(
  (state: RootState, property: string) => property
);

/**
 * Selects the current value of a nested property, checking area, container, and UI states.
 * @param {Object} state - The Redux state.
 * @param {...string} args - The nested property path to select.
 * @returns {*} The current value of the specified nested property.
 */
export const getCurrentValue2 = createCachedSelector(
  getAreaProperty2,
  getContainerProperty2,
  getUIProperty,
  (state: RootState, ...args: any[]) => {
    return isString(args) ? args : remove(args, (x) => x !== undefined).join('.')
  },
  (areaValue, containerValue, uiValue) => areaValue ?? containerValue ?? uiValue ?? null
)(
  (state: RootState, ...args: any[]) => {
    const activeTab = getActiveTab(state);
    const property = isString(args) ? args : remove(args, (x) => x !== undefined).join(':');
    return `${activeTab}:${property}`;
  }
);

/**
 * Selector that computes the bounding box and center for one or multiple areas.
 *
 * @function
 * @name getAreaBoxes
 * @param {Object} state - The Redux state.
 * @returns {Array} An array of objects where each object represents a single area's bounding box and center
 *  . Each object contains:
 *   @property {string} id - The unique identifier of the area.
 *   @property {number} top - The top position of the area.
 *   @property {number} left - The left position of the area.
 *   @property {number} length - The length of the area.
 *   @property {number} width - The width of the area.
 *   @property {number} cx - The x-coordinate of the area's center.
 *   @property {number} cy - The y-coordinate of the area's center.
 *
 * @description
 * This selector processes the area data from the state and calculates the bounding box
 * and center coordinates for each area. It returns an array of objects.
 *
 * @example
 * const areaBoxes = useSelector(getAreaBoxes);
 * areaBoxes.forEach(box => console.log(box.id, box.cx, box.cy));
 */
export const getAreaBoxes = createSelector(
  getAreaArray,
  (areas) => {
    return areas.map(area => {
      const { id, location, dimensions } = area
      const center = { cx: location.left + dimensions.length / 2, cy: location.top + dimensions.width / 2 };
      return { id, ...location, ...dimensions, ...center }
    })
  }
);

/**
* @function getAreaGrid
* @description Creates a memoized selector that calculates grid parameters for room heatmap plots.
*
* @param {Object} state - Redux state object
* @param {number} [spacing=1] - Grid spacing value in meters/feet
*
* @returns {Object} Grid parameters for heatmap calculations
* @property {number} gridSpacing - Distance between grid points
* @property {number} gridXOffset - X-axis offset to center grid
* @property {number} gridYOffset - Y-axis offset to center grid
* @property {number} gridX - Number of grid points in X direction
* @property {number} gridY - Number of grid points in Y direction
*
* @example
* const gridParams = getAreaGrid(state, 0.5);
* // Returns {gridSpacing: 0.5, gridXOffset: 0.25, gridYOffset: 0.1, gridX: 21, gridY: 15}
*/
export const getAreaGrid = createCachedSelector(
  getAreaBoxes,
  (state: RootState, spacing = 1) => spacing,
  (areaBox, gridSpacing = 1) => {
    const { top, left, length, width } = areaBox[0];
    const gridX = ceil((length - left) / gridSpacing) + 1;
    const gridY = ceil((width - top) / gridSpacing) + 1;
    const gridXOffset = round(((length - left) - (gridX * gridSpacing)) / 2, 2);
    const gridYOffset = round(((width - top) - (gridY * gridSpacing)) / 2, 2);
    return {
      gridSpacing,
      gridXOffset,
      gridYOffset,
      gridX,
      gridY
    }
  }
)(
  (state: RootState, spacing = 1) => `${spacing}`
);

/**
* @function getAreaFineGrid
* @description Creates a memoized selector that calculates grid parameters for room heatmap plots.
*
* @param {Object} state - Redux state object
* @param {number} [spacing=.1] - Grid spacing value in meters/feet
*
* @returns {Object} Grid parameters for heatmap calculations
* @property {number} gridSpacing - Distance between grid points
* @property {number} gridXOffset - X-axis offset to center grid
* @property {number} gridYOffset - Y-axis offset to center grid
* @property {number} gridX - Number of grid points in X direction
* @property {number} gridY - Number of grid points in Y direction
*
* @example
* const gridParams = getAreaGrid(state, 0.5);
* // Returns {gridSpacing: 0.5, gridXOffset: 0.25, gridYOffset: 0.1, gridX: 21, gridY: 15}
*/
export const getAreaFineGrid = createCachedSelector(
  getAreaBoxes,
  (state: RootState, spacing = .1) => spacing,
  (areaBox, gridSpacing = .1) => {
    const { top, left, length, width } = areaBox[0];
    const gridX = ceil((length - left) / gridSpacing) + 1;
    const gridY = ceil((width - top) / gridSpacing) + 1;
    const gridXOffset = round(((length - left) - (gridX * gridSpacing)) / 2, 2);
    const gridYOffset = round(((width - top) - (gridY * gridSpacing)) / 2, 2);
    return {
      gridSpacing,
      gridXOffset,
      gridYOffset,
      gridX,
      gridY
    }
  }
)(
  (state: RootState, spacing = .1) => `${spacing}`
);
